import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { getMe, login as apiLogin, logout as apiLogout } from "../api/auth.ts";
import type { MeResponse } from "../api/auth.ts";

interface AuthState {
  user: MeResponse | null;
  token: string | null;
  isLoading: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const TOKEN_KEY = "aios_token";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    isLoading: true,
  });

  // Rehydrate token from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (stored == null) {
      setState((s) => ({ ...s, isLoading: false }));
      return;
    }

    getMe(stored)
      .then((user) => setState({ user, token: stored, isLoading: false }))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setState({ user: null, token: null, isLoading: false });
      });
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await apiLogin(email, password);
    localStorage.setItem(TOKEN_KEY, res.token);
    const user = await getMe(res.token);
    setState({ user, token: res.token, isLoading: false });
  }, []);

  const logout = useCallback(async () => {
    const token = state.token;
    localStorage.removeItem(TOKEN_KEY);
    setState({ user: null, token: null, isLoading: false });
    if (token != null) {
      await apiLogout(token).catch(() => undefined); // best-effort
    }
  }, [state.token]);

  return (
    <AuthContext.Provider value={{ ...state, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx == null) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
